import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Mic, RotateCcw, Gift } from "lucide-react";
import { TOK, SANS } from "../../pages/Soulumo/SignatureBits";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` } });
const cache = {};
const myClone = () => {
  const t = localStorage.getItem("reclaim_token") || "";
  cache[t] ||= axios.get(`${API}/api/voice-clone/me`, auth()).then((r) => r.data).catch(() => ({}));
  return cache[t];
};

const WHY = {
  clone_not_ready: "Record your voice first, in My Voice.",
  clone_not_enabled: "My Voice isn't switched on for your account.",
  over_allowance: "You've used this month's voice allowance.",
  clone_cap_reached: "You've used this month's installations in your voice.",
  already_rendering: "This one is already being made. Give it a moment.",
  already_your_voice: "This one is already in your voice.",
};

// One tap: re-make an older installation in the listener's own cloned voice.
export const RevoiceButton = ({ j, onChanged }) => {
  const [clone, setClone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { myClone().then(setClone); }, []);
  const gifted = !!j.saved_from_slug;
  if (j.audio_status !== "ready" || !clone || (clone.voice_id && j.narrator_voice_id === clone.voice_id)) return null;
  if (!clone.voice_id || !clone.enabled) return gifted ? <GiftVoiceSetup j={j} /> : null;

  const go = async () => {
    const what = gifted ? `This replaces the gifted version with one in your own voice.` : "";
    if (!window.confirm(`Re-make "${j.name}" in your own voice? ${what}${clone.premium ? " Free on Premium." : " It uses one of this month's installations in your voice."}`)) return;
    setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/api/audio-journeys/${j.id}/revoice`, {}, auth());
      onChanged?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const code = (typeof d === "string" ? d : d?.code || "").split(":")[0];
      setMsg(WHY[code] || "That didn't work just now. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3">
      <button type="button" onClick={go} disabled={busy} data-testid={`library-revoice-${j.id}`}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[0.62rem] uppercase tracking-[0.2em] transition-opacity duration-200 disabled:opacity-50"
        style={{ border: `1px solid ${TOK.gold}`, color: TOK.gold, fontFamily: SANS, minHeight: 40 }}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Mic size={12} />} Re-make in my voice
      </button>
      {msg && <p className="mt-2 text-xs" style={{ color: "#e0857a" }} data-testid={`library-revoice-msg-${j.id}`}>{msg}</p>}
    </div>
  );
};

export const REVOICE_AFTER_KEY = "soulumo_revoice_after";

// Saved gift, no voice yet: keep the sender's voice, or set up My Voice and come back to re-make it.
const GiftVoiceSetup = ({ j }) => (
  <div className="mt-3" data-testid={`library-gift-voice-${j.id}`}>
    <p className="text-xs italic" style={{ color: TOK.creamMist, fontFamily: '"Cormorant Garamond", serif', fontSize: "0.95rem" }}>
      {j.gift_from_name ? `Happy with ${j.gift_from_name}'s voice? Keep it as it is.` : "Happy with this voice? Keep it as it is."} Or hear these words in your own.
    </p>
    <button type="button" data-testid={`library-gift-voice-setup-${j.id}`}
      onClick={() => { sessionStorage.setItem(REVOICE_AFTER_KEY, JSON.stringify({ id: j.id, name: j.name })); window.location.href = "/voice-clone"; }}
      className="mt-2 inline-flex items-center gap-2 rounded-full px-4 py-2 text-[0.62rem] uppercase tracking-[0.2em] transition-opacity hover:opacity-80"
      style={{ border: `1px solid ${TOK.gold}`, color: TOK.gold, fontFamily: SANS, minHeight: 40 }}>
      <Mic size={12} /> Re-make in my own voice
    </button>
  </div>
);

// Back on My Voice with a ready voice: one tap re-makes the gift they came from.
export const RevoicePending = () => {
  const [p] = useState(() => { try { return JSON.parse(sessionStorage.getItem(REVOICE_AFTER_KEY)); } catch { return null; } });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  if (!p?.id) return null;
  const go = async () => {
    setBusy(true); setMsg("");
    try {
      await axios.post(`${API}/api/audio-journeys/${p.id}/revoice`, {}, auth());
      sessionStorage.removeItem(REVOICE_AFTER_KEY);
      window.location.href = `/my-library?open=${p.id}`;
    } catch (e) {
      const d = e?.response?.data?.detail;
      setMsg(WHY[(typeof d === "string" ? d : d?.code || "").split(":")[0]] || "That didn't work just now. Please try again.");
      setBusy(false);
    }
  };
  return (
    <div className="mb-8 rounded-2xl p-5" style={{ border: `1px solid ${TOK.gold}`, background: "rgba(224,169,122,0.08)" }} data-testid="revoice-pending-card">
      <p className="text-sm" style={{ color: TOK.cream, fontFamily: SANS }}>Your voice is ready. Re-make &ldquo;{p.name}&rdquo; in your own voice now?</p>
      <button type="button" onClick={go} disabled={busy} data-testid="revoice-pending-go"
        className="mt-3 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[0.62rem] uppercase tracking-[0.2em] disabled:opacity-50"
        style={{ background: TOK.gold, color: TOK.ink, fontFamily: SANS }}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Mic size={12} />} Re-make it in my voice
      </button>
      <button type="button" onClick={() => { sessionStorage.removeItem(REVOICE_AFTER_KEY); window.location.reload(); }} data-testid="revoice-pending-skip"
        className="ml-4 text-xs underline-offset-4 hover:underline" style={{ color: TOK.creamMist, fontFamily: SANS }}>Not now</button>
      {msg && <p className="mt-2 text-xs" style={{ color: "#e0857a" }} data-testid="revoice-pending-msg">{msg}</p>}
    </div>
  );
};

// Failed installation: build it again from its saved words and settings.
export const RetryButton = ({ j, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const go = async () => {
    setBusy(true); setMsg("");
    try { await axios.post(`${API}/api/audio-journeys/${j.id}/retry`, {}, auth()); onChanged?.(); }
    catch { setMsg("This one can't be rebuilt. Please create it again."); }
    finally { setBusy(false); }
  };
  return (
    <span className="inline-flex flex-col">
      <button type="button" onClick={go} disabled={busy} data-testid={`library-retry-${j.id}`}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[0.62rem] uppercase tracking-[0.2em] disabled:opacity-50"
        style={{ border: `1px solid ${TOK.gold}`, color: TOK.gold, fontFamily: SANS, minHeight: 40 }}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />} Try again
      </button>
      {msg && <span className="mt-1 text-xs" style={{ color: "#e0857a" }}>{msg}</span>}
    </span>
  );
};

// A gifted installation: one tap mints its private gift link and opens the share sheet.
export const SendGiftButton = ({ j }) => {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const go = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/audio-journeys/${j.id}/share`, {}, auth());
      const url = `${window.location.origin}${r.data.url}`;
      const text = `${j.gift.for}, I made this for you.`;
      if (navigator.share) await navigator.share({ title: j.name, text, url }).catch(() => {});
      else {
        try { await navigator.clipboard.writeText(`${text} ${url}`); setDone("Link copied"); }
        catch { setDone(url); }
      }
    } catch { setDone("Couldn't make the link. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-3">
      <button type="button" onClick={go} disabled={busy} data-testid={`library-send-gift-${j.id}`}
        className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[0.62rem] uppercase tracking-[0.2em]"
        style={{ background: TOK.gold, color: TOK.ink, fontFamily: SANS, minHeight: 40 }}>
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Gift size={12} />} Send to {j.gift.for}
      </button>
      {done && <span className="ml-3 text-xs break-all select-all" style={{ color: TOK.creamMist }} data-testid={`library-send-gift-msg-${j.id}`}>{done}</span>}
    </div>
  );
};
